import { useState } from "react";
import {
  UserPlus,
  MoreVertical,
  X,
  Loader2,
  ShieldCheck,
  ShieldAlert,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/utils";
import { adminFetch } from "@/lib/api";
import { useFetch } from "@/hooks/use-fetch";
import { PageLoading } from "@/components/loading";
import { ErrorMessage } from "@/components/error-message";

// API returns snake_case fields with is_active as 0/1 integer
interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: string;
  is_active: number;
  last_login: string | null;
  created_at: string;
}

interface UsersResponse {
  data: AdminUser[];
}

export function UsersPage() {
  const [showAddModal, setShowAddModal] = useState(false);
  const { data, loading, error, refetch } = useFetch<UsersResponse>("/users");

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Admin kullanıcıları yönetin
        </p>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90"
        >
          <UserPlus className="h-4 w-4" />
          Yeni Kullanıcı
        </button>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {loading ? (
          <PageLoading />
        ) : error ? (
          <ErrorMessage message={error} onRetry={refetch} className="m-4" />
        ) : !data || data.data.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-muted-foreground">
            Henüz kullanıcı bulunmuyor.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    İsim
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    E-posta
                  </th>
                  <th className="hidden px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground md:table-cell">
                    Rol
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Durum
                  </th>
                  <th className="hidden px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground sm:table-cell">
                    Son Giriş
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    İşlem
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.data.map((user) => (
                  <UserRow key={user.id} user={user} onUpdate={refetch} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add user modal */}
      {showAddModal && (
        <AddUserModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}

function UserRow({
  user,
  onUpdate,
}: {
  user: AdminUser;
  onUpdate: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [updating, setUpdating] = useState(false);

  // is_active comes as 0/1 integer from SQLite
  const isActive = !!user.is_active;

  async function toggleActive() {
    setUpdating(true);
    try {
      await adminFetch(`/users/${user.id}`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: !isActive }),
      });
      onUpdate();
    } catch {
      // handled
    } finally {
      setUpdating(false);
      setMenuOpen(false);
    }
  }

  return (
    <tr className="transition-colors hover:bg-secondary/50">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-xs font-medium text-foreground">
            {user.name
              .split(" ")
              .map((n) => n[0])
              .join("")
              .toUpperCase()
              .slice(0, 2)}
          </div>
          <span className="text-sm font-medium text-foreground">
            {user.name}
          </span>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-muted-foreground">{user.email}</span>
      </td>
      <td className="hidden px-4 py-3 md:table-cell">
        <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
          {user.role === "super_admin" ? (
            <>
              <ShieldAlert className="h-3.5 w-3.5 text-accent" />
              Süper Admin
            </>
          ) : (
            <>
              <ShieldCheck className="h-3.5 w-3.5" />
              Admin
            </>
          )}
        </span>
      </td>
      <td className="px-4 py-3">
        <span
          className={cn(
            "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
            isActive
              ? "bg-success/15 text-success"
              : "bg-destructive/15 text-destructive"
          )}
        >
          {isActive ? "Aktif" : "Pasif"}
        </span>
      </td>
      <td className="hidden px-4 py-3 sm:table-cell">
        <span className="text-sm text-muted-foreground">
          {user.last_login
            ? formatDate(user.last_login)
            : "Henüz giriş yapılmadı"}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="relative">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            aria-label="İşlemler"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
          {menuOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setMenuOpen(false)}
              />
              <div className="absolute right-0 top-full z-20 mt-1 w-40 rounded-lg border border-border bg-card py-1 shadow-lg">
                <button
                  onClick={toggleActive}
                  disabled={updating}
                  className="flex w-full items-center px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-50"
                >
                  {updating ? (
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  ) : null}
                  {isActive ? "Pasif Yap" : "Aktif Yap"}
                </button>
              </div>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

function AddUserModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("admin");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;

    setSubmitting(true);
    setError("");
    try {
      await adminFetch("/users", {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          role,
        }),
      });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Bir hata oluştu");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">
            Yeni Kullanıcı Ekle
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Kapat"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          {error && (
            <div className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">
              İsim
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              placeholder="Ad Soyad"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">
              E-posta
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              placeholder="admin@example.com"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">
              Rol
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            >
              <option value="admin">Admin</option>
              <option value="super_admin">Süper Admin</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={submitting || !name.trim() || !email.trim()}
              className={cn(
                "inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90",
                (submitting || !name.trim() || !email.trim()) &&
                  "cursor-not-allowed opacity-50"
              )}
            >
              {submitting && (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              )}
              Ekle
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
